import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { useApiMutation } from "@/lib/api";
import { useToast } from "@/hooks/useToast";
import { BillingPeriod } from "@/types/billing";
import { ManualEntry } from "./ManualEntry";
import { SettlementPanel } from "./SettlementPanel";
import { MissingSummary, listMissing } from "./MissingSummary";

const CO_LOCATIONS = ["krasinskiego"];

export function CollectingView({ period, refetch, location = null }: { period: BillingPeriod; refetch: () => void; location?: string | null }) {
  const [correctionOpen, setCorrectionOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { mutate, loading } = useApiMutation<{ estimated_promoted: string[] }>();
  const { push } = useToast();
  const missing = listMissing(period.collection_status);
  const [periodYear, periodMonth] = period.period_id.split("-").map(Number);

  async function advance() {
    try {
      const r = await mutate("/api/billing/period/advance", "POST");
      const n = r.estimated_promoted?.length ?? 0;
      push({
        message: n > 0 ? `Zakończono inkasowanie (${n} pól oszacowano)` : "Zakończono inkasowanie",
        tone: "success",
      });
      setConfirmOpen(false);
      refetch();
    } catch (err) {
      push({ message: (err as Error).message, tone: "danger" });
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Advance button — top right, matching InvoicingView layout */}
      <div className="flex justify-end">
        <Button onClick={() => setConfirmOpen(true)}>Zakończ inkasowanie ▶</Button>
      </div>

      {/* Collection status table — filtered by selected location */}
      <MissingSummary
        status={
          location
            ? Object.fromEntries(
                Object.entries(period.collection_status).filter(([loc]) => loc === location)
              )
            : period.collection_status
        }
        year={periodYear}
        month={periodMonth}
        refetch={refetch}
      />

      {/* Correction entry — full width via modal */}
      <div className="flex justify-start">
        <Button variant="secondary" iconLeft={<Plus className="size-3.5" />}
                onClick={() => setCorrectionOpen(true)}>
          Dodaj korektę / pozycję dodatkową
        </Button>
      </div>

      <Modal
        open={correctionOpen}
        onClose={() => setCorrectionOpen(false)}
        title="Wpis korekty / pozycja dodatkowa"
        size="md"
      >
        <ManualEntry
          periodId={period.period_id}
          onSaved={refetch}
          onClose={() => setCorrectionOpen(false)}
        />
      </Modal>

      {/* CO settlement — only shown for applicable location */}
      {CO_LOCATIONS.filter((loc) => location === null || location === loc).map((loc) => (
        <SettlementPanel
          key={loc}
          location={loc}
          cell={period.collection_status[loc]?.settlement ?? { status: "optional", source: null, collected_at: null }}
          onChanged={refetch}
        />
      ))}

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={advance}
        title="Zakończyć inkasowanie?"
        confirmLabel="Zakończ"
        loading={loading}
        description={
          missing.length === 0 ? (
            <p>Wszystkie pola są wypełnione. Czy chcesz przejść do etapu fakturowania?</p>
          ) : (
            <>
              <p className="mb-2">Następujące pola zostaną oznaczone jako <em>oszacowane</em>:</p>
              <ul className="text-xs font-mono">
                {missing.map((m) => <li key={m}>{m}</li>)}
              </ul>
            </>
          )
        }
      />
    </div>
  );
}
